import {
  AlignmentType,
  Paragraph,
  ImageRun,
  TextRun,
  Table,
  TableRow,
  TableCell,
  ShadingType,
  ExternalHyperlink,
} from "docx";
import html2canvas from "html2canvas";

//=== Docx Element Creation Functions ===

/**
 * Convert an imported image to a buffer for docx
 * @param {string} imagePath - Path to the image
 * @returns {Uint8Array} - Image data as buffer
 */
const convertImageToBuffer = (imagePath) => {
  // For webpack-imported images, we need to fetch them and convert to buffer
  return fetch(imagePath)
    .then((response) => response.arrayBuffer())
    .then((buffer) => new Uint8Array(buffer));
};

/**
 * Create a header paragraph
 * @param {string} text - The text of the header
 * @param {number} level - The level of the header
 * @returns {Paragraph} - The header paragraph
 */
const buildHeader = (text, level, isCentered = false) => {
  return new Paragraph({
    children: [
      new TextRun({
        text: text,
      }),
    ],
    alignment: isCentered ? AlignmentType.CENTER : AlignmentType.LEFT,
    style: `IRIPHeading${level}`,
  });
};

/**
 * Create a paragraph
 * @param {string} text - The text of the paragraph
 * @returns {Paragraph} - The paragraph
 */
const buildParagraph = (text, { bold = false, italics = false } = {}) => {
  return new Paragraph({
    children: [
      new TextRun({
        text: text,
        bold: bold,
        italics: italics,
        size: 24, // 12pt
        color: "000000",
      }),
    ],
    style: "IRIPNormal",
  });
};

const buildNewlines = (count = 1) => {
  return Array.from(
    { length: count },
    () =>
      new Paragraph({
        children: [new TextRun({ text: "" })],
        style: "IRIPNormal",
      })
  );
};

const buildPageBreak = () => {
  return new Paragraph({
    children: [new TextRun({ text: "" })],
    style: "IRIPNormal",
    pageBreak: true,
  });
};

const buildInlineHeader = (texts, level, isCentered = false) => {
  return buildTextRun(texts, isCentered, `IRIPHeading${level}`);
};

const buildInlineParagraph = (textsWithOptions, isCentered = false) => {
  return buildTextRun(textsWithOptions, isCentered, "IRIPNormal");
};

/**
 * Create a paragraph with multiple text runs
 * @param {Array} textRuns - Array of text run objects with properties: text, textColor, bold, italic
 * @param {boolean} isCentered - Whether the paragraph should be centered
 * @returns {Paragraph} - The paragraph with multiple text runs
 */
const buildTextRun = (textRuns, isCentered = false, style = "IRIPNormal") => {
  // Create an array of TextRun objects from the textRuns array
  const children = textRuns.map((run) => {
    return new TextRun({
      text: run.text,
      color: run.color || "000000",
      bold: run.bold || false,
      italics: run.italics || false,
      size: 24, // 12pt
      color: "000000",
    });
  });

  // Create and return the paragraph with the text runs
  return new Paragraph({
    children,
    alignment: isCentered ? AlignmentType.CENTER : AlignmentType.LEFT,
    style,
  });
};

/**
 * Renders a React component, captures it as an image, and returns an ImageRun for document
 * @param {React.ComponentType} Component - React component to render
 * @param {Object} props - Props to pass to the component
 * @param {Object} options - Capture and image options
 * @param {number} [options.pageWidth=550] - Default page width to fill
 * @param {number} [options.scale=2] - Scale factor for rendering (higher = better quality)
 * @param {string} [options.title] - Image title for alt text
 * @param {string} [options.description] - Image description for alt text
 * @returns {Promise<ImageRun|null>} - Promise that resolves to an ImageRun instance or null if failed
 */
const renderElementToImageRunById = async (elementId, options = {}) => {
  const element = document.querySelector(`#${elementId}`);
  if (!element) {
    console.error("Target element not found");
    return null;
  }

  try {
    const canvas = await html2canvas(element);

    if (canvas.width === 0 || canvas.height === 0) {
      console.error(
        "Canvas has zero width or height. Skipping image generation."
      );
      return null;
    }

    const dataUrl = canvas.toDataURL("image/png");
    const base64String = dataUrl.replace(/^data:image\/png;base64,/, "");

    // Decode base64 string to binary data
    const binaryString = atob(base64String);
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i);
    }

    // Calculate proportional dimensions based on canvas aspect ratio
    const pageWidth = options.pageWidth || 550; // Default page width in points
    const aspectRatio = canvas.height / canvas.width;
    const width = options.width || pageWidth;
    const height = options.height || Math.round(width * aspectRatio);

    return new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new ImageRun({
          type: "png",
          data: byteArray,
          transformation: {
            width: width,
            height: height,
          },
        }),
      ],
    });
  } catch (error) {
    console.error("Error rendering element to image run:", error);
    return null;
  }
};

/**
 * Creates an external hyperlink that can be added to a paragraph
 * @param {string} text - The text to display for the link
 * @param {string} url - The URL to link to
 * @param {Object} options - Styling options
 * @param {boolean} [options.bold=false] - Whether text should be bold
 * @param {number} [options.size=24] - Font size in half-points
 * @returns {ExternalHyperlink} - An external hyperlink object that can be added to a paragraph
 */
const createExternalLink = (text, url, options = {}) => {
  const { bold = false, size = 24 } = options;

  return new ExternalHyperlink({
    children: [
      new TextRun({
        text: text,
        style: "Hyperlink",
        bold: bold,
        size: size,
      }),
    ],
    link: url,
  });
};

// Tables
const createTableHeaderCell = (text) => {
  return createTableCell({
    text: text,
    shadingColor: "000000",
    fontColor: "FFFFFF",
  });
};

const createTableCell = ({
  text,
  shadingColor = "FFFFFF",
  shadingType = ShadingType.CLEAR,
  fontColor = "000000",
  bold = false,
  fontSize = 24,
}) => {
  return new TableCell({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: text,
            bold: bold,
            color: fontColor,
            size: fontSize,
          }),
        ],
      }),
    ],
    shading: {
      fill: shadingColor,
      type: shadingType,
      color: "auto",
    },
  });
};

/**
 * Create a paragraph with a hyperlink
 * @param {string} text - The text to display for the link
 * @param {string} url - The URL to link to
 * @param {Object} options - Styling options
 * @param {boolean} [options.bold=false] - Whether text should be bold
 * @param {boolean} [options.italics=false] - Whether text should be italic
 * @returns {Paragraph} - The paragraph containing the hyperlink
 */
const buildHyperlink = (text, url, { bold = false, italics = false } = {}) => {
  return new Paragraph({
    children: [
      new ExternalHyperlink({
        children: [
          new TextRun({
            text: text,
            bold: bold,
            italics: italics,
            size: 24, // 12pt
            color: "0000FF", // Blue color for hyperlinks
          }),
        ],
        link: url,
      }),
    ],
    style: "IRIPNormal",
  });
};

//#endregion

export {
  convertImageToBuffer,
  buildHeader,
  buildParagraph,
  buildTextRun,
  renderElementToImageRunById,
  createExternalLink,
  createTableHeaderCell,
  createTableCell,
  buildNewlines,
  buildPageBreak,
  buildInlineHeader,
  buildInlineParagraph,
  buildHyperlink,
};
